<template>
  <!-- Calendar table -->
  <table class="w-full">
    <thead class="sticky top-0 z-20 bg-white">
      <tr class="flex">
        <!-- Empty header cell for user names column -->
        <th
          class="sticky left-0 z-30 flex-shrink-0 w-12 p-1 text-center border-b border-r border-solid sm:w-40 border-neutral-200 bg-neutral-100 h-11"
        ></th>
        <!-- Header cells for each day in the month -->
        <th
    v-for="day in daysInMonth"
    :key="day"
    class="flex items-center justify-center flex-grow flex-shrink-0 p-2 text-sm font-bold text-center border-b border-r border-solid border-neutral-200 w-11 h-11"
    :class="{ 'bg-neutral-100 text-neutral-600': !isCurrentDay(day), 
              'bg-neutral-200 text-neutral-700': isCurrentDay(day) 
    }"
  >
          <span class="cursor-default">{{ day }}</span>
        </th>
      </tr>
    </thead>
    <tbody>
      <!-- Rows for each user -->
      <tr
        v-for="user in users"
        :key="user.id"
        class="flex group hover:bg-neutral-50"
      >
        <!-- User name cell -->
        <td
          class="sticky left-0 z-10 flex-shrink-0 w-12 text-sm bg-white border-b border-r border-solid cursor-pointer sm:w-40 group-hover:bg-neutral-50 sticky-column border-neutral-200"
          @mouseover="showUserInfo(user, $event)"
          @mouseleave="$emit('hide-user-info')"
        >
          <div class="px-2 w-full h-full overflow-hidden text-ellipsis whitespace-nowrap leading-[2.75rem]" :title="user.first_name + ' ' + user.last_name">
            <span class="sm:hidden">{{ usersStore.getUserInitials(user) }}</span>
            <span class="hidden sm:inline">{{ user.first_name + " " + user.last_name }}</span>
          </div>
        </td>
        <!-- Cells for each day in the month -->
        <td
          v-for="day in daysInMonth"
          :key="`day-${day}-${currentMonth}-${currentYear}`"
          class="flex flex-grow flex-shrink-0 p-0 border-b border-solid border-neutral-200"
        >
          <EventCell
            :day="{ date: day, month: currentMonth, year: currentYear }"
            :event="getEventForUserAndDay(user, day)"
            :current-year="currentYear"
            size="large"
            class="flex flex-grow flex-shrink-0"
            @show-event-info="showEventInfo"
            @hide-event-info="$emit('hide-event-info')"
            @edit-replacement="$emit('edit-replacement', $event)"
          />
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>
import { computed, defineEmits, defineProps, inject } from "vue";
import EventCell from "@/components/ui/AdminDashboard/EventCell.vue";

// Inject users store
const usersStore = inject("usersStore");

// Define component props
const props = defineProps({
  currentDate: {
    type: Date,
    required: true,
  },
  users: {
    type: Array,
    required: true,
  },
});

// Define custom events to emit
const emit = defineEmits([
  "add-or-update-event",
  "show-user-info",
  "hide-user-info",
  "show-event-info",
  "hide-event-info",
  "edit-replacement",
]);

// Computed properties for current month and year
const currentMonth = computed(() => props.currentDate.getMonth());
const currentYear = computed(() => props.currentDate.getFullYear());

// Computed property for days in the current month
const daysInMonth = computed(() => {
  return new Date(currentYear.value, currentMonth.value + 1, 0).getDate();
});

// Function to get event for a user on a specific day
function getEventForUserAndDay(user, day) {
  return user.events.find((event) => {
    const eventDate = new Date(event.date);
    return (
      eventDate.getDate() === day &&
      eventDate.getMonth() === currentMonth.value &&
      eventDate.getFullYear() === currentYear.value
    );
  });
}

// Function to show user info popover
function showUserInfo(user, event) {
  emit("show-user-info", user, event.clientX, event.clientY);
}

// Function to show event info popover
function showEventInfo(event, clientX, clientY) {
  emit("show-event-info", event, clientX, clientY);
}

// Function to check if a day is the current day
const isCurrentDay = (day) => {
  const today = new Date();
  return (
    day === today.getDate() &&
    currentMonth.value === today.getMonth() &&
    currentYear.value === today.getFullYear()
  );
};
</script>